<template>
  <div :class="['min-h-screen flex flex-col', darkMode ? 'dark bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800' : 'bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200']">
    
    <!-- Login -->
    <div v-if="!authenticated" class="flex items-center justify-center min-h-screen p-4">
      <div class="bg-white/90 dark:bg-slate-800/90 rounded-3xl p-6 sm:p-8 max-w-sm w-full border border-white/20 dark:border-slate-700/50 shadow-2xl">
        <div class="flex justify-center mb-6">
          <img 
            v-if="!darkMode"
            :src="branding.logoLight" 
            :alt="branding.companyName" 
            class="h-10 w-auto"
          />
          <img 
            v-else
            :src="branding.logoDark" 
            :alt="branding.companyName" 
            class="h-10 w-auto"
          />
        </div>
        <h1 class="text-xl font-bold text-slate-800 dark:text-white text-center mb-1">{{ branding.appTitle }}</h1>
        <p class="text-sm text-slate-500 dark:text-slate-400 text-center mb-6">Telefonbuch mit Teams-Integration</p>
        
        <!-- Teams SSO Loading State -->
        <div v-if="ssoLoading || (runningInTeams && !ssoFailed)" class="text-center py-4">
          <div class="flex items-center justify-center gap-2 mb-3">
            <svg class="w-5 h-5 animate-spin text-blue-600" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
            </svg>
            <span class="text-sm text-slate-600 dark:text-slate-300 font-medium">Teams SSO wird versucht...</span>
          </div>
          <div class="w-48 mx-auto h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
            <div class="h-full bg-blue-500 rounded-full animate-pulse" style="width: 60%"></div>
          </div>
          <p class="text-xs text-slate-400 mt-2">Automatische Anmeldung über Microsoft Teams</p>
        </div>
        
        <!-- Manual Login Button (shown in browser or when Teams SSO failed) -->
        <button 
          v-else
          @click="login" 
          class="w-full bg-slate-800 hover:bg-slate-900 dark:bg-slate-600 dark:hover:bg-slate-500 text-white font-medium py-3 rounded-xl flex items-center justify-center gap-2 transition-colors hover:shadow-lg"
        >
          <svg class="w-5 h-5" viewBox="0 0 21 21" fill="currentColor">
            <path d="M0 0h10v10H0V0zm11 0h10v10H11V0zM0 11h10v10H0V11zm11 0h10v10H11V11z"/>
          </svg>
          Mit Microsoft anmelden
        </button>
        
        <!-- Teams SSO failed hint -->
        <p v-if="ssoFailed && !ssoLoading" class="text-xs text-amber-500 text-center mt-2">
          Teams SSO fehlgeschlagen — bitte manuell anmelden
        </p>
        
        <!-- SSO Debug Panel -->
        <div class="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
          <button 
            @click="showSsoDebug = !showSsoDebug"
            class="w-full text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 flex items-center justify-center gap-1"
          >
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
            </svg>
            {{ showSsoDebug ? 'Debug ausblenden' : 'SSO Debug anzeigen' }}
          </button>
          
          <div v-if="showSsoDebug" class="mt-3 p-3 bg-slate-100 dark:bg-slate-900 rounded-lg text-left">
            <div class="flex items-center justify-between mb-2">
              <span class="text-xs font-semibold text-slate-600 dark:text-slate-400">SSO Debug Info</span>
              <span 
                class="text-xs px-2 py-0.5 rounded-full"
                :class="runningInTeams ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400' : 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400'"
              >
                {{ runningInTeams ? '📱 In Teams' : '🌐 Browser' }}
              </span>
            </div>
            
            <div v-if="ssoDebugLog.length === 0" class="text-xs text-slate-400 italic">
              Keine SSO-Logs verfügbar
            </div>
            
            <div v-else class="space-y-2 max-h-48 overflow-y-auto">
              <div 
                v-for="(log, idx) in ssoDebugLog" 
                :key="idx"
                class="text-xs p-2 rounded bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
              >
                <div class="flex items-center gap-2 mb-1">
                  <span class="font-mono text-[10px] text-slate-400">{{ log.timestamp.split('T')[1].split('.')[0] }}</span>
                  <span class="font-semibold text-slate-600 dark:text-slate-300">[{{ log.step }}]</span>
                </div>
                <div class="text-slate-700 dark:text-slate-300">{{ log.message }}</div>
                <pre v-if="log.data" class="mt-1 text-[10px] text-slate-500 dark:text-slate-400 overflow-x-auto whitespace-pre-wrap break-all">{{ log.data }}</pre>
              </div>
            </div>
          </div>
        </div>
        
        <div class="flex justify-center mt-4">
          <button @click="toggleDarkMode" class="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-xl transition-colors hover:bg-slate-100 dark:hover:bg-slate-700">
            <Moon v-if="!darkMode" class="w-5 h-5" />
            <Sun v-else class="w-5 h-5" />
          </button>
        </div>
        
        <p class="text-xs text-slate-400 text-center mt-6">© {{ new Date().getFullYear() }} {{ branding.companyName }}</p>
      </div>
    </div>

    <!-- Main -->
    <div v-else class="flex flex-col min-h-screen">
      <!-- Header (hidden in monitor mode) -->
      <header v-if="!isMonitorMode" class="sticky top-0 z-50 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-b border-slate-200/50 dark:border-slate-700/50">
        <div class="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div class="flex items-center gap-2 sm:gap-3">
            <img 
              v-if="!darkMode"
              :src="branding.logoLight" 
              :alt="branding.companyName" 
              class="h-6 sm:h-7 w-auto"
            />
            <img 
              v-else
              :src="branding.logoDark" 
              :alt="branding.companyName" 
              class="h-6 sm:h-7 w-auto"
            />
            <span class="hidden sm:inline font-semibold text-slate-800 dark:text-white">{{ branding.appTitle }}</span>
            <span 
              class="text-xs px-2 py-0.5 rounded-full font-medium transition-colors" 
              :class="isConnected 
                ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400' 
                : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 animate-pulse'"
            >
              {{ isConnected ? '● Live' : '○ Offline' }}
            </span>
          </div>
          
          <div class="flex items-center gap-0.5 sm:gap-1">
            <div class="hidden sm:flex items-center gap-2 px-2 py-1 text-sm text-slate-600 dark:text-slate-300">
              <div v-if="userPhotoUrl" class="w-7 h-7 rounded-full overflow-hidden ring-2 ring-white/50 dark:ring-slate-700/50">
                <img :src="userPhotoUrl" class="w-full h-full object-cover" />
              </div>
              <div v-else class="w-7 h-7 rounded-full bg-gradient-to-br from-slate-500 to-slate-700 text-white text-xs flex items-center justify-center font-medium">
                {{ userInitials }}
              </div>
              <span class="max-w-24 truncate">{{ userInfo?.name }}</span>
            </div>
            
            <button @click="toggleDarkMode" class="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-xl transition-colors hover:bg-slate-100 dark:hover:bg-slate-700/50">
              <Moon v-if="!darkMode" class="w-4 h-4" />
              <Sun v-else class="w-4 h-4" />
            </button>

            <!-- Call Mode Toggle -->
            <button
              @click="setCallMode(callMode === 'teams' ? 'native' : 'teams')"
              class="p-2 rounded-xl transition-colors"
              :class="callMode === 'teams'
                ? 'text-purple-600 bg-purple-50 dark:bg-purple-900/30 dark:text-purple-400'
                : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700/50'"
              :title="callMode === 'teams' ? 'Telefonate über Teams (klicken für natives Telefon)' : 'Natives Telefon (klicken für Teams)'"
            >
              <PhoneCall class="w-4 h-4" />
            </button>
            
            <button v-if="isAdmin" @click="showAdmin = !showAdmin" class="p-2 rounded-xl transition-colors" :class="showAdmin ? 'text-blue-600 bg-blue-100 dark:bg-blue-900/40' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700/50'">
              <Settings class="w-4 h-4" />
            </button>
            
            <button @click="handleLogout" class="p-2 text-slate-400 hover:text-red-500 rounded-xl transition-colors hover:bg-red-50 dark:hover:bg-red-900/20">
              <LogOut class="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <AdminDashboard v-if="showAdmin && isAdmin" @close="showAdmin = false" />

      <main v-else :class="isMonitorMode ? 'flex-1' : 'flex-1 max-w-[1800px] mx-auto w-full px-4 sm:px-6 lg:px-8 py-3 sm:py-4'">
        <ContactList @monitor-mode-changed="onMonitorModeChanged" />
      </main>
      
      <footer v-if="!isMonitorMode" class="border-t border-slate-200/50 dark:border-slate-700/50 bg-white/90 dark:bg-slate-800/90 py-3 text-center text-xs text-slate-400">
        {{ branding.appTitle }} © {{ new Date().getFullYear() }} {{ branding.companyName }}
      </footer>

      <!-- Digitale Visitenkarte FAB -->
      <button
        v-if="!isMonitorMode"
        @click="showMyCard = true"
        title="Meine Visitenkarte"
        class="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-2xl ring-2 ring-white/40 dark:ring-slate-700/60 hover:scale-110 active:scale-95 transition-all duration-200"
      >
        <div class="relative w-full h-full">
          <img
            v-if="userPhotoUrl"
            :src="userPhotoUrl"
            class="w-full h-full rounded-full object-cover"
          />
          <div
            v-else
            class="w-full h-full rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center"
          >
            <span class="text-lg font-bold text-white">{{ userInitials }}</span>
          </div>
          <!-- Badge icon -->
          <div class="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center shadow-md border-2 border-white dark:border-slate-900">
            <CreditCard class="w-2.5 h-2.5 text-white" />
          </div>
        </div>
      </button>

      <MyBusinessCard v-if="showMyCard" @close="showMyCard = false" />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { Settings, LogOut, Moon, Sun, PhoneCall, CreditCard } from 'lucide-vue-next';
import { isAuthenticated, getUserInfo, oauthLogin, logout, handleRedirectCallback, authenticatedFetch, initializeTeams, branding, getSsoDebugLog, getIsInTeams, clearAuthData, notifyTeamsAppLoaded } from './oauth2-config';
import { isConnected, fetchFilters, fetchPresence, registerSocketUser } from './websocketService';
import { getMonitorMode } from './localStorageService';
import { callMode, setCallMode } from './callService';
import ContactList from './components/ContactList.vue';
import AdminDashboard from './components/AdminDashboard.vue';
import MyBusinessCard from './components/MyBusinessCard.vue';

const authenticated = ref(false);
const userInfo = ref(null);
const isAdmin = ref(false);
const showAdmin = ref(false);
const darkMode = ref(false);
const userPhotoUrl = ref(null);
const showSsoDebug = ref(false);
const ssoDebugLog = ref([]);
const runningInTeams = ref(false);
const ssoLoading = ref(false);
const ssoFailed = ref(false);
const isMonitorMode = ref(getMonitorMode());
const showMyCard = ref(false);

const onMonitorModeChanged = (enabled) => {
  isMonitorMode.value = enabled;
};

const API_URL = process.env.VUE_APP_API_URL || '__VUE_APP_API_URL__';

const userInitials = computed(() => {
  if (!userInfo.value?.name) return '?';
  return userInfo.value.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
});

const toggleDarkMode = () => {
  darkMode.value = !darkMode.value;
  localStorage.setItem(`${branding.storagePrefix}_darkmode`, darkMode.value ? 'dark' : 'light');
  if (darkMode.value) document.documentElement.classList.add('dark');
  else document.documentElement.classList.remove('dark');
};

onMounted(async () => {
  // Listen for auth state changes (e.g. token expired mid-session)
  // In Teams: automatically re-authenticate via SSO instead of showing login screen
  window.addEventListener('authStateChanged', async (e) => {
    if (!e.detail.authenticated) {
      if (runningInTeams.value) {
        console.log('[Auth] Token expired in Teams — attempting SSO renewal...');
        const ssoSuccess = await initializeTeams();
        if (ssoSuccess) {
          console.log('[Auth] SSO renewal successful — session restored');
          userInfo.value = getUserInfo();
          authenticated.value = true;
          loadUserDataAfterValidation();
          authenticatedFetch(`${API_URL}/api/me`)
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d) isAdmin.value = d.isAdmin; })
            .catch(() => {});
          return;
        }
        console.warn('[Auth] SSO renewal also failed — showing login');
      }
      authenticated.value = false;
      userInfo.value = null;
    }
  });

  // Initialize dark mode early
  const saved = localStorage.getItem(`${branding.storagePrefix}_darkmode`);
  if (saved === 'dark') darkMode.value = true;
  else if (!saved) darkMode.value = window.matchMedia('(prefers-color-scheme: dark)').matches;
  if (darkMode.value) document.documentElement.classList.add('dark');

  // Check for OAuth redirect callback FIRST (browser flow)
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has('code')) {
    try { await handleRedirectCallback(); } catch (e) { console.error(e); }
  }

  // Fast path: if token exists and is locally valid (not expired JWT), show UI immediately
  // and validate against /api/me in the background — eliminates SSO flicker on tab return.
  if (isAuthenticated()) {
    const token = localStorage.getItem('id_token');
    const parts = token?.split('.') || [];
    let tokenLocallyValid = false;
    if (parts.length === 3) {
      try {
        // base64url → base64 conversion required before atob()
        const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = b64.padEnd(b64.length + (4 - b64.length % 4) % 4, '=');
        const payload = JSON.parse(atob(padded));
        tokenLocallyValid = payload.exp * 1000 > Date.now();
      } catch (e) { /* malformed token — fall through to SSO */ }
    }

    if (tokenLocallyValid) {
      // Show authenticated UI immediately without waiting for network roundtrip
      authenticated.value = true;
      userInfo.value = getUserInfo();
      loadUserDataAfterValidation();
      initializeTeams().then(() => {
        runningInTeams.value = getIsInTeams();
        notifyTeamsAppLoaded();
      });
      // Validate token against backend in background
      authenticatedFetch(`${API_URL}/api/me`)
        .then(res => res.ok ? res.json() : Promise.reject(new Error(`api/me ${res.status}`))
        ).then(meData => {
          isAdmin.value = meData.isAdmin;
        }).catch(e => {
          console.warn('Background token validation failed, clearing session:', e.message);
          clearAuthData();
          authenticated.value = false;
          userInfo.value = null;
        });
      return;
    }
    // Token malformed or expired — clear stale tokens so initializeTeams() does fresh SSO
    console.warn('Clearing stale auth tokens before SSO attempt');
    clearAuthData();
  }

  // Not yet authenticated — attempt Teams SSO
  ssoLoading.value = true;
  const ssoSuccess = await initializeTeams();
  
  // Update debug info
  runningInTeams.value = getIsInTeams();
  ssoDebugLog.value = getSsoDebugLog();
  
  if (ssoSuccess) {
    // SSO succeeded — token is fresh from OBO exchange, trust it immediately
    sessionStorage.removeItem('sso_reload_count');
    console.log('Teams SSO authentication successful');
    userInfo.value = getUserInfo();
    authenticated.value = true;
    ssoLoading.value = false;
    notifyTeamsAppLoaded();
    loadUserDataAfterValidation();
    // Admin check in background — non-blocking
    authenticatedFetch(`${API_URL}/api/me`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) isAdmin.value = d.isAdmin; })
      .catch(() => {});
    return;
  }

  // SSO failed in Teams — auto-reload once (the user said navigating away and back fixes it)
  if (runningInTeams.value && !isAuthenticated()) {
    const reloadCount = parseInt(sessionStorage.getItem('sso_reload_count') || '0');
    if (reloadCount < 2) {
      sessionStorage.setItem('sso_reload_count', String(reloadCount + 1));
      console.log(`[SSO] Auto-reload attempt ${reloadCount + 1}/2...`);
      // Brief delay then reload — mimics the manual "navigate away and back"
      setTimeout(() => window.location.reload(), 1500);
      return; // Keep showing the loading spinner during reload
    }
    // Already reloaded twice, give up — show manual login
    sessionStorage.removeItem('sso_reload_count');
    ssoFailed.value = true;
  }
  ssoLoading.value = false;

  // Check auth state after callback processing
  authenticated.value = isAuthenticated();
  
  if (authenticated.value) {
    userInfo.value = getUserInfo();
    await loadUserData();
  }
});

// Safety net: recover from Teams tab suspension during SSO
// When Teams suspends the webview mid-SSO and resumes later, the async SSO flow
// may have completed (tokens in localStorage) but authenticated.value is still false.
const handleVisibilityChange = () => {
  if (document.hidden || authenticated.value) return;
  if (!isAuthenticated()) return;
  const token = localStorage.getItem('id_token');
  const parts = token?.split('.') || [];
  if (parts.length !== 3) return;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64.padEnd(b64.length + (4 - b64.length % 4) % 4, '=');
    const payload = JSON.parse(atob(padded));
    if (payload.exp * 1000 <= Date.now()) return;
  } catch (e) { return; }
  // Token is valid but UI is stuck — recover
  console.log('[Visibility] Recovering from suspended SSO — token valid in localStorage');
  userInfo.value = getUserInfo();
  authenticated.value = true;
  ssoLoading.value = false;
  loadUserDataAfterValidation();
  authenticatedFetch(`${API_URL}/api/me`)
    .then(r => r.ok ? r.json() : null)
    .then(d => { if (d) isAdmin.value = d.isAdmin; })
    .catch(() => {});
};
document.addEventListener('visibilitychange', handleVisibilityChange);

/** Load user data (admin check, photo, filters, presence) */
const loadUserData = async () => {
  try {
    const res = await authenticatedFetch(`${API_URL}/api/me`);
    if (res.ok) isAdmin.value = (await res.json()).isAdmin;
  } catch (e) { console.error(e); }

  loadUserDataAfterValidation();
};

/** Load photo, filters, presence (called after token is confirmed valid) */
const loadUserDataAfterValidation = async () => {
  // Register socket user for targeted notifications (e.g. call assignments)
  registerSocketUser();

  try {
    const photoRes = await authenticatedFetch(`${API_URL}/api/me/photo`);
    if (photoRes.ok) userPhotoUrl.value = URL.createObjectURL(await photoRes.blob());
  } catch (e) {}

  await fetchFilters();
  fetchPresence();
};

const login = () => oauthLogin();
const handleLogout = () => logout();
</script>
